import React, { useState, useRef, useEffect } from 'react';
import { Tooltip, Typography } from '@mui/material';
import './style.css';

/**
 * TruncatedText Component
 * 
 * A component that truncates text after a specified number of words and shows
 * a tooltip with the full text on hover.
 * 
 * @param {string} text - The text to display
 * @param {number} maxWords - Maximum number of words to show before truncating (default: 10)
 * @param {string} className - CSS class name
 * @param {object} tooltipSx - Custom styles for the tooltip
 * @param {object} style - Custom styles for the text element
 * @param {string} variant - Typography variant (default: 'body1')
 * @param {boolean} showStats - Whether to show text statistics for very long content (default: true)
 */
const TruncatedText = ({ 
  text, 
  maxWords = 10, 
  className, 
  tooltipSx = {},
  style = {},
  variant = 'body1',
  showStats = true
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const timeoutRef = useRef(null);

  
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const truncateText = (text, maxWords) => {
    if (!text) return '';
    const words = text?.split(' ');
    if (words?.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  const truncatedText = truncateText(text, maxWords);
  const shouldShowTooltip = text?.split(' ')?.length > maxWords;

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setIsHovered(false);
      }
    }, 300); 
  };

  const handleTooltipMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsTooltipHovered(true);
  };

  const handleTooltipMouseLeave = () => {
    setIsTooltipHovered(false);
    setIsHovered(false);
  };

  
  const TooltipContent = () => {
    const wordCount = text?.split(' ')?.length || 0;
    const charCount = text?.length || 0;
    const isVeryLong = wordCount > 100 || charCount > 1000;
    const isScrollable = charCount > 2000; 

    return (
      <div
        className="tooltip-scrollable-content"
        style={{
          maxHeight: '300px',
          maxWidth: '400px',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '8px 0',
          lineHeight: '1.5',
          fontSize: '14px',
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          textAlign: 'left',
        }}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      >
        {text}
      </div>
    );
  };

  return (
    <Tooltip 
      title={shouldShowTooltip ? <TooltipContent /> : ''} 
      arrow 
      placement="top"
      open={isHovered && shouldShowTooltip}
      onOpen={() => setIsHovered(true)}
      onClose={() => setIsHovered(false)}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'rgba(6, 49, 100, 0.95)',
            color: 'white',
            fontSize: '14px',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: "var(--shadow-xl)",
            border: '1px solid rgba(212, 148, 58, 0.3)',
            maxWidth: '400px',
            maxHeight: '350px',
            overflow: 'hidden',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            lineHeight: '1.5',
            animation: 'tooltipFadeIn 0.3s ease-in-out',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            },
            ...tooltipSx
          }
        },
        arrow: {
          sx: {
            color: 'rgba(6, 49, 100, 0.95)'
          }
        }
      }}
    >
      <Typography 
        variant={variant}
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ 
          cursor: shouldShowTooltip ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out',
          ...style
        }}
      >
        {truncatedText}
      </Typography>
    </Tooltip>
  );
};

export default TruncatedText; 